import { Button } from '@design-system';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import {
  getProductRef,
  getProductStatus,
} from '../../../../helpers/purchasing';
import { useTimeout } from '../../../../hooks/useTimeout';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../../types/Purchasing';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import { ProductCountdown } from '../../ProductDate/ProductCountdown';
import { SignUpButton } from '../SignUpButton';

type Props<T extends ProductPurchaseDetails> = {
  purchasingController: ProductPurchasingController<T>;
  onBuyNowClick: () => void;
  product: ProductType;
};

export const TimedPurchaseCta = ({
  onBuyNowClick,
  product,
  purchasingController,
}: Props<ProductPurchaseDetails>) => {
  const { t } = useTranslation('product');
  const [showDialog, setShowDialog] = useState(false);
  const isClosed = useTimeout(purchasingController.product?.closingDate);
  const isRemindMeEnabled = useFeatureIsOn('product-page-remind-me');

  const disabled = !purchasingController.canPurchase || isClosed;

  return (
    <div className="w-full" data-testid="timed-purchase-cta">
      <ProductCountdown date={purchasingController.product?.closingDate} />
      <div className="flex w-full flex-col items-center gap-3">
        <Button
          disabled={disabled}
          fullWidth
          label={t('goToCheckout')}
          loading={purchasingController.checkoutLoading}
          onClick={onBuyNowClick}
          variant="primary"
        />
        {isRemindMeEnabled ? (
          <SignUpButton
            labels={{ normal: t('remindMe'), success: t('subscribed') }}
            product={product}
            productRef={getProductRef(purchasingController)}
            productStatus={getProductStatus(product, purchasingController)}
            showIcon
            variant="tertiary"
          />
        ) : null}
        <button
          className="hyperlink"
          onClick={() => setShowDialog(true)}
          type="button"
        >
          {t('subscriptionCta.timed.learnMoreDialog.title')}
        </button>
        <SimpleDialog
          onClose={() => setShowDialog(false)}
          open={showDialog}
          title={t('subscriptionCta.timed.learnMoreDialog.title')}
        >
          <p className="mb-4 text-neutral-6">
            {t('subscriptionCta.timed.learnMoreDialog.paragraph1')}
          </p>
          <p className="text-neutral-6">
            {t('subscriptionCta.timed.learnMoreDialog.paragraph2')}
          </p>
        </SimpleDialog>
      </div>
    </div>
  );
};
